import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";

const SUBSCRIBERS = new Set();
let cached = { alerts: [], fleetStatus: {}, loaded: false };
let inFlight = null;
let pollTimer = null;

async function refresh() {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const [alertsRes, fleetRes] = await Promise.all([
        api.get("/etm/alerts").catch(() => ({ data: [] })),
        api.get("/etm/fleet-status").catch(() => ({ data: {} })),
      ]);
      cached = {
        alerts: alertsRes.data || [],
        fleetStatus: fleetRes.data || {},
        loaded: true,
      };
      SUBSCRIBERS.forEach((cb) => cb(cached));
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

export function refreshETMSummary() {
  return refresh();
}

// Wipe cached state — call on logout so the next user doesn't briefly see
// the previous user's alerts/fleet before the first poll lands.
export function resetETMSummary() {
  cached = { alerts: [], fleetStatus: {}, loaded: false };
  inFlight = null;
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  SUBSCRIBERS.forEach((cb) => cb(cached));
}

// Shared summary: fetches once across all subscribers, polls every 60s.
//
// `enabled` lets a caller opt out of the fetch entirely without changing
// the return shape. Used by Sidebar to skip /etm/* fetches for roles the
// backend forbids (owner, accountant, ground_support) — without this gate,
// every owner page load fired a noisy 403 against /etm/alerts and
// /etm/fleet-status. Default true keeps existing callers unchanged.
export default function useETMSummary({ enabled = true } = {}) {
  const [state, setState] = useState(cached);
  const mounted = useRef(true);

  useEffect(() => {
    if (!enabled) return undefined;
    mounted.current = true;
    const cb = (next) => {
      if (mounted.current) setState(next);
    };
    SUBSCRIBERS.add(cb);
    if (!cached.loaded) refresh();
    if (!pollTimer) {
      pollTimer = setInterval(refresh, 60000);
    }
    return () => {
      mounted.current = false;
      SUBSCRIBERS.delete(cb);
      if (SUBSCRIBERS.size === 0 && pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };
  }, [enabled]);

  // `/etm/alerts?status=active` already returns only open alerts (active +
  // acknowledged — both are unresolved). Count them both: an acknowledged
  // alert is still something the user has to close out.
  const isOpen = (a) => a.status === "active" || a.status === "acknowledged";
  const actionCount = state.alerts.filter((a) => a.severity === "action" && isOpen(a)).length;
  const watchCount = state.alerts.filter((a) => a.severity === "watch" && isOpen(a)).length;
  const topActionAlert = state.alerts.find((a) => a.severity === "action" && isOpen(a)) || null;

  const stateCounts = { learning: 0, confidence_building: 0, mature: 0 };
  for (const id in state.fleetStatus) {
    const st = state.fleetStatus[id]?.baseline_status?.state;
    if (st && stateCounts[st] != null) stateCounts[st] += 1;
  }

  return {
    loaded: state.loaded,
    alerts: state.alerts,
    fleetStatus: state.fleetStatus,
    actionCount,
    watchCount,
    topActionAlert,
    stateCounts,
    refresh,
  };
}
